// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import { eq } from "drizzle-orm"
import * as Log from "@opencode-ai/core/util/log"
import { ProjectTable } from "@/project/project.sql"
import { ProjectID } from "@/project/schema"
import * as Project from "@/project/project"
import { MessageTable, PartTable, SessionTable } from "@/session/session.sql"
import type { MessageID, PartID, SessionID } from "@/session/schema"
import { MessageV2 } from "@/session/message-v2"
import { Database } from "@/storage/db"
import { readSessionIndex, resolveWorkspaceDirectory, type LegacySessionIndexEntry } from "./discovery"
import { mapLegacyToolCallParts, type LegacyToolCall } from "./session-tool-mapper"
import {
  addSessionMigrationItem,
  createSessionMigrationReport,
  type SessionMigrationItem,
  type SessionMigrationReport,
} from "./types"
import { errorMessage, isDirectory, isRecord, readJsonSafe, stableHash, truncateString } from "./util"

const LOG_PREFIX = "[infcode:migration:sessions]"
const log = Log.create({ service: LOG_PREFIX })
const OUTPUT_LIMIT = 1024 * 1024

type LegacySessionFile = {
  id?: unknown
  sessionId?: unknown
  sessionID?: unknown
  title?: unknown
  workspaceDirectory?: unknown
  directory?: unknown
  cwd?: unknown
  history?: unknown
  messages?: unknown
}

type LegacyHistoryItem = Record<string, unknown>

type ProjectTarget = {
  id: ProjectID
  directory: string
}

export namespace SessionsMigration {
  export type Item = SessionMigrationItem
  export type Report = SessionMigrationReport

  export async function run(input: { home: string }): Promise<SessionMigrationReport> {
    const report = createSessionMigrationReport()
    const sessionsDir = path.join(input.home, ".infcode", "sessions")
    if (!(await isDirectory(sessionsDir))) return report

    const index = await readSessionIndex(input.home)
    for (const issue of index.issues) {
      addSessionMigrationItem(report, {
        sourcePath: issue.sourcePath,
        status: "failed",
        reason: issue.kind,
        message: issue.message,
      })
    }

    const entries = await fs.readdir(sessionsDir, { withFileTypes: true }).catch(() => [])
    for (const entry of entries.sort((a, b) => a.name.localeCompare(b.name))) {
      if (!entry.isFile()) continue
      if (!entry.name.endsWith(".json")) continue
      if (entry.name === "sessions.json") continue
      const sourcePath = path.join(sessionsDir, entry.name)
      if (entry.name.endsWith("_log.json")) {
        report.summary.logSkipped++
        continue
      }
      await migrateSessionFile({ report, sourcePath, index: index.entries })
    }
    return report
  }
}

async function migrateSessionFile(input: {
  report: SessionMigrationReport
  sourcePath: string
  index: Map<string, LegacySessionIndexEntry>
}) {
  const parsed = await readJsonSafe<LegacySessionFile>(input.sourcePath)
  if (!parsed.ok) {
    addSessionMigrationItem(input.report, {
      sourcePath: input.sourcePath,
      status: "failed",
      reason: "session-json-invalid",
      message: parsed.message,
    })
    log.warn("failed to read v1 session json", { sourcePath: input.sourcePath, error: parsed.message })
    return
  }

  const legacySessionId = legacyID(parsed.value, input.sourcePath)
  const mappedSessionId = sessionID(input.sourcePath, legacySessionId)
  if (sessionExists(mappedSessionId)) {
    addSessionMigrationItem(input.report, {
      legacySessionId,
      mappedSessionId,
      sourcePath: input.sourcePath,
      status: "skipped",
      reason: "target-exists",
    })
    return
  }

  const indexEntry = input.index.get(legacySessionId)
  const directory = directoryFor(parsed.value, indexEntry)
  if (!directory) {
    addSessionMigrationItem(input.report, {
      legacySessionId,
      mappedSessionId,
      sourcePath: input.sourcePath,
      status: "skipped",
      reason: "workspace-missing",
    })
    return
  }

  const baseTime = indexEntry?.created ?? (await fileMtime(input.sourcePath))
  const project = await projectTarget(directory)
  const history = historyItems(parsed.value)
  const degradedReasons: string[] = []

  Database.transaction((db) => {
    db.insert(ProjectTable)
      .values({
        id: project.id,
        worktree: project.directory,
        vcs: "git",
        time_created: baseTime,
        time_updated: baseTime,
        sandboxes: [],
      })
      .onConflictDoNothing()
      .run()

    db.insert(SessionTable)
      .values({
        id: mappedSessionId,
        project_id: project.id,
        slug: legacySessionId,
        directory: project.directory,
        path: null,
        title: titleFor(parsed.value, indexEntry, legacySessionId),
        version: "v1-migrated",
        time_created: baseTime,
        time_updated: baseTime + history.length,
      })
      .onConflictDoNothing()
      .run()

    const toolOutputs = collectToolOutputs(history)
    let nearestUser: MessageID | undefined
    let messageOrdinal = 0
    for (let historyIndex = 0; historyIndex < history.length; historyIndex++) {
      const item = history[historyIndex]
      const role = historyRole(item)
      if (role !== "user" && role !== "assistant") continue

      const mappedMessageId = messageID(input.sourcePath, legacySessionId, historyIndex)
      const created = baseTime + historyIndex
      const turnID = role === "user" ? mappedMessageId : (nearestUser ?? mappedMessageId)
      db.insert(MessageTable)
        .values({
          id: mappedMessageId,
          session_id: mappedSessionId,
          turn_id: turnID,
          time_created: created,
          time_updated: created,
          data: messageData({
            role,
            messageID: mappedMessageId,
            parentID: turnID,
            created,
            directory: project.directory,
          }),
        })
        .onConflictDoNothing()
        .run()
      if (role === "user") nearestUser = mappedMessageId

      const parts = partsForHistoryItem({
        sourcePath: input.sourcePath,
        legacySessionId,
        sessionID: mappedSessionId,
        messageID: mappedMessageId,
        item,
        historyIndex,
        created,
        directory: project.directory,
        toolOutputs,
        degradedReasons,
      })
      for (let partIndex = 0; partIndex < parts.length; partIndex++) {
        const part = parts[partIndex]
        const { id, sessionID: _sessionID, messageID: _messageID, ...data } = part
        db.insert(PartTable)
          .values({
            id,
            session_id: mappedSessionId,
            message_id: mappedMessageId,
            time_created: created + partIndex,
            time_updated: created + partIndex,
            data,
          })
          .onConflictDoNothing()
          .run()
      }
      messageOrdinal++
    }

    if (messageOrdinal === 0) {
      const mappedMessageId = messageID(input.sourcePath, legacySessionId, 0)
      db.insert(MessageTable)
        .values({
          id: mappedMessageId,
          session_id: mappedSessionId,
          turn_id: mappedMessageId,
          time_created: baseTime,
          time_updated: baseTime,
          data: messageData({
            role: "assistant",
            messageID: mappedMessageId,
            parentID: mappedMessageId,
            created: baseTime,
            directory: project.directory,
          }),
        })
        .onConflictDoNothing()
        .run()
    }
  })

  addSessionMigrationItem(input.report, {
    legacySessionId,
    mappedSessionId,
    sourcePath: input.sourcePath,
    directory: project.directory,
    projectId: project.id,
    status: "migrated",
    ...(degradedReasons.length > 0 ? { degradedReasons } : {}),
  })
}

function partsForHistoryItem(input: {
  sourcePath: string
  legacySessionId: string
  sessionID: SessionID
  messageID: MessageID
  item: LegacyHistoryItem
  historyIndex: number
  created: number
  directory: string
  toolOutputs: Map<string, unknown>
  degradedReasons: string[]
}): MessageV2.Part[] {
  const parts: MessageV2.Part[] = []
  const content = contentText(historyContent(input.item))
  if (content) {
    parts.push({
      id: partID(input.sourcePath, input.legacySessionId, input.historyIndex, "text"),
      sessionID: input.sessionID,
      messageID: input.messageID,
      type: "text",
      text: content,
      time: { start: input.created, end: input.created },
    })
  }

  const reasoningValue = historyReasoning(input.item)
  const reasoning = reasoningText(reasoningValue)
  if (reasoning) {
    const record = isRecord(reasoningValue) ? reasoningValue : {}
    parts.push({
      id: partID(input.sourcePath, input.legacySessionId, input.historyIndex, "reasoning"),
      sessionID: input.sessionID,
      messageID: input.messageID,
      type: "reasoning",
      text: reasoning,
      time: {
        start: timestamp(record.startTime ?? record.startAt, input.created),
        end: timestamp(record.endTime ?? record.endAt, input.created),
      },
      metadata: { legacy: true },
    })
  }

  const toolCalls = toolCallStates(input.item)
  for (let index = 0; index < toolCalls.length; index++) {
    if (!isRecord(toolCalls[index])) continue
    const toolCall = toolCalls[index] as LegacyToolCall
    const callID = legacyToolCallID(toolCall) ?? `tool-${index}`
    const output = input.toolOutputs.get(callID)
    const mapped = mapLegacyToolCallParts({
      sessionID: input.sessionID,
      messageID: input.messageID,
      partID: partID(input.sourcePath, input.legacySessionId, input.historyIndex, callID),
      created: input.created,
      directory: input.directory,
      toolCall,
      output,
    })
    for (const part of mapped) parts.push(truncatePart(part, input.degradedReasons))
  }

  return parts
}

function truncatePart(part: MessageV2.Part, degradedReasons: string[]): MessageV2.Part {
  if (part.type !== "tool" || part.state.status !== "completed") return part
  const truncated = truncateString(part.state.output, OUTPUT_LIMIT)
  if (!truncated.truncated) return part
  degradedReasons.push(`tool-output-truncated:${part.callID}`)
  return {
    ...part,
    state: {
      ...part.state,
      output: truncated.text,
      metadata: {
        ...part.state.metadata,
        legacyOutputTruncated: true,
        legacyOutputOmittedChars: truncated.omitted,
      },
    },
  }
}

function collectToolOutputs(history: LegacyHistoryItem[]) {
  const result = new Map<string, unknown>()
  for (const item of history) {
    const message = historyMessage(item)
    const role = text(message.role) ?? text(item.role)
    if (role !== "tool") continue
    const callID =
      text(message.toolCallId) ??
      text(message.tool_call_id) ??
      text(message.callID) ??
      text(message.id) ??
      text(item.toolCallId) ??
      text(item.tool_call_id) ??
      text(item.callID) ??
      text(item.id)
    if (!callID || result.has(callID)) continue
    result.set(callID, message.output ?? message.content ?? message.result ?? item.output ?? item.content ?? item.result)
  }
  return result
}

// Cache to avoid redundant git probes + DB upserts when multiple legacy sessions share the same directory.
// N sessions in dir → 1 call to Project.fromDirectory instead of N.
const projectTargetCache = new Map<string, Promise<ProjectTarget>>()

function projectTarget(directory: string): Promise<ProjectTarget> {
  const cached = projectTargetCache.get(directory)
  if (cached) return cached

  const promise = resolveProjectTarget(directory)
  projectTargetCache.set(directory, promise)
  return promise
}

async function resolveProjectTarget(directory: string): Promise<ProjectTarget> {
  if (!(await isDirectory(directory))) return syntheticProjectTarget(directory)

  try {
    const { project } = await Project.fromDirectory(directory)
    if (project.id !== ProjectID.global) {
      return {
        id: project.id,
        directory: project.worktree,
      }
    }
  } catch (err) {
    log.warn("failed to resolve v1 session project from directory", { directory, error: errorMessage(err) })
  }

  return syntheticProjectTarget(directory)
}

function syntheticProjectTarget(directory: string): ProjectTarget {
  return {
    id: `legacy-project:${stableHash(directory)}` as ProjectID,
    directory,
  }
}

function messageData(input: {
  role: "user" | "assistant"
  messageID: MessageID
  parentID: MessageID
  created: number
  directory: string
}): Omit<MessageV2.Info, "id" | "sessionID"> {
  if (input.role === "user") {
    return {
      role: "user",
      time: { created: input.created },
      agent: "legacy",
      model: { providerID: "legacy", modelID: "legacy" },
    } as Omit<MessageV2.User, "id" | "sessionID">
  }
  return {
    role: "assistant",
    time: { created: input.created, completed: input.created },
    parentID: input.parentID,
    modelID: "legacy",
    providerID: "legacy",
    mode: "legacy",
    agent: "legacy",
    path: {
      cwd: input.directory,
      root: input.directory,
    },
    cost: 0,
    tokens: {
      input: 0,
      output: 0,
      reasoning: 0,
      cache: { read: 0, write: 0 },
    },
  } as Omit<MessageV2.Assistant, "id" | "sessionID">
}

function sessionExists(id: SessionID) {
  return Boolean(Database.use((db) => db.select({ id: SessionTable.id }).from(SessionTable).where(eq(SessionTable.id, id)).get()))
}

function legacyID(session: LegacySessionFile, sourcePath: string) {
  return text(session.id) ?? text(session.sessionId) ?? text(session.sessionID) ?? path.basename(sourcePath, ".json")
}

function sessionID(sourcePath: string, legacySessionId: string) {
  return `ses_migrated_${stableHash({ sourcePath, legacySessionId })}` as SessionID
}

function messageID(sourcePath: string, legacySessionId: string, historyIndex: number) {
  return `msg_migrated_${stableHash({ sourcePath, legacySessionId, historyIndex })}` as MessageID
}

function partID(sourcePath: string, legacySessionId: string, historyIndex: number, partKey: string) {
  return `prt_migrated_${stableHash({ sourcePath, legacySessionId, historyIndex, partKey })}` as PartID
}

function titleFor(session: LegacySessionFile, indexEntry: LegacySessionIndexEntry | undefined, legacySessionId: string) {
  return indexEntry?.title ?? text(session.title) ?? legacySessionId
}

function directoryFor(session: LegacySessionFile, indexEntry: LegacySessionIndexEntry | undefined): string | undefined {
  const raw = indexEntry?.workspaceDirectory ?? text(session.workspaceDirectory) ?? text(session.directory) ?? text(session.cwd)
  if (raw) return resolveWorkspaceDirectory(raw)
  return undefined
}

function historyItems(session: LegacySessionFile): LegacyHistoryItem[] {
  const source = Array.isArray(session.history) ? session.history : Array.isArray(session.messages) ? session.messages : []
  return source.filter(isRecord)
}

function historyMessage(item: LegacyHistoryItem): Record<string, unknown> {
  return isRecord(item.message) ? item.message : item
}

function historyRole(item: LegacyHistoryItem) {
  return text(historyMessage(item).role) ?? text(item.role)
}

function historyContent(item: LegacyHistoryItem) {
  const message = historyMessage(item)
  return message.content ?? item.content ?? item.text
}

function historyReasoning(item: LegacyHistoryItem) {
  const message = historyMessage(item)
  return item.reasoning ?? message.reasoning
}

function toolCallStates(item: LegacyHistoryItem) {
  const message = historyMessage(item)
  if (Array.isArray(item.toolCallStates)) return item.toolCallStates
  if (Array.isArray(message.toolCallStates)) return message.toolCallStates
  return []
}

function legacyToolCallID(toolCall: LegacyToolCall) {
  const nested = isRecord(toolCall.toolCall) ? toolCall.toolCall : {}
  return text(toolCall.id) ?? text(toolCall.callID) ?? text(toolCall.toolCallId) ?? text(nested.id)
}

async function fileMtime(sourcePath: string) {
  try {
    return (await fs.stat(sourcePath)).mtimeMs
  } catch (err) {
    log.warn("failed to read v1 session mtime", { sourcePath, error: errorMessage(err) })
    return Date.now()
  }
}

function reasoningText(value: unknown) {
  if (typeof value === "string") return value
  if (isRecord(value)) return contentText(value.text ?? value.content)
  return undefined
}

function contentText(value: unknown): string | undefined {
  if (typeof value === "string") return value
  if (!Array.isArray(value)) return undefined
  const text = value
    .flatMap((item) => {
      if (typeof item === "string") return [item]
      if (!isRecord(item)) return []
      const blockText = item.text ?? item.content
      return typeof blockText === "string" ? [blockText] : []
    })
    .join("")
  return text.length > 0 ? text : undefined
}

function timestamp(value: unknown, fallback: number) {
  if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.floor(value))
  if (typeof value === "string") {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return Math.max(0, Math.floor(fallback))
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}
